import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useCapabilities } from "@components/catalog/useCapabilities";
import { CAPABILITIES } from "@api/capabilities";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { InlineRowGrid, type InlineColumn } from "@components/input";
import { CatalogPicker } from "@components/catalog";
import { useMoney } from "@units/useMoney";
import { useGetAfeLinesQuery, useCreateAfeLineMutation, useUpdateAfeLineMutation } from "@api/pApi";
import type { AfeLineOut, AccrualMethod, CatalogValue } from "@api/types";
import type { MessageKey } from "@i18n/messages";
import { useCatalogLabels } from "@hooks/useCatalogLabels";
import "./afe.css";

/** The four accrual methods, in the order they surface in the picker. */
const METHODS: { id: AccrualMethod; key: MessageKey }[] = [
  { id: "by_quantity", key: "afe.method.by_quantity" },
  { id: "lump", key: "afe.method.lump" },
  { id: "pct_complete", key: "afe.method.pct_complete" },
  { id: "milestone", key: "afe.method.milestone" },
];

/** A draft for the entry row (all string-typed; validated on commit). */
interface AfeDraft {
  account: string | null;
  accountValue: CatalogValue | null;
  description: string;
  method: AccrualMethod;
  unit: string;
  qty: string;
  budget: string;
}

const emptyDraft = (): AfeDraft => ({
  account: null,
  accountValue: null,
  description: "",
  method: "by_quantity",
  unit: "",
  qty: "",
  budget: "",
});

/**
 * AFE Management panel (§11). The operator plans AFE line items against the
 * ops program-step sequence; a service company self-serves the lines scoped to
 * it. Each line carries an accrual method (how it accrues / devengado, §21f) —
 * the server computes `accrued` from the method + the cost account's treatment
 * (Avance | Devengado, blank ⇒ Avance); the FE only displays it. Every money
 * field is server-computed; roll-up totals sum the served lines for display.
 */
export function AfePanel({ eventId }: { eventId: string }) {
  const { t } = useLocale();
  const { money } = useMoney();
  const { can } = useCapabilities();
  // AFE account codes are cost_accounts catalog keys (the CatalogPicker below
  // writes cost_accounts entries). Resolve to the Spanish display_label; an
  // unresolved code falls back to its raw key so the cell is never blank.
  const accountLabel = useCatalogLabels("cost_accounts");
  const [selectedStep, setSelectedStep] = useState<string | null>(null);

  const {
    data: lines = [],
    isLoading,
    isError,
    refetch,
  } = useGetAfeLinesQuery(eventId, {
    skip: !eventId,
  });
  const [createLine] = useCreateAfeLineMutation();
  const [updateLine] = useUpdateAfeLineMutation();

  // Capability scope (§ACL): a user WITHOUT afe:manage standing in for a service
  // company sees & edits only its own (service-owned) lines; a holder of
  // afe:manage (the operator / cost engineer) sees the whole plan and plans
  // operator lines. This mirrors §11's operator-plan vs service-self-serve split.
  const isOperator = can(CAPABILITIES.afeManage);
  const scopedLines = useMemo(
    () => (isOperator ? lines : lines.filter((l) => l.owner_party === "service")),
    [lines, isOperator],
  );

  // Program-step sequence — derived from the (scoped) lines, preserving first
  // appearance order. Selecting a step filters the line grid below.
  const steps = useMemo(() => {
    const seen = new Map<string, { budget: number; accrued: number }>();
    for (const l of scopedLines) {
      const step = l.program_step ?? "—";
      const acc = seen.get(step) ?? { budget: 0, accrued: 0 };
      acc.budget += l.budget;
      acc.accrued += l.accrued;
      seen.set(step, acc);
    }
    return [...seen.entries()].map(([step, v]) => ({ step, ...v }));
  }, [scopedLines]);

  const activeStep = selectedStep ?? steps[0]?.step ?? null;
  const gridLines = useMemo(
    () => scopedLines.filter((l) => (l.program_step ?? "—") === activeStep),
    [scopedLines, activeStep],
  );

  // Currency: single ISO when all lines agree, else null (show each native).
  const ccy = useMemo(() => {
    const set = new Set(scopedLines.map((l) => l.currency ?? "USD"));
    return set.size === 1 ? [...set][0]! : null;
  }, [scopedLines]);

  // Roll-up totals across the scoped lines (display-only sum of served values).
  const totals = useMemo(() => {
    return scopedLines.reduce(
      (a, l) => ({
        budget: a.budget + l.budget,
        committed: a.committed + l.committed,
        accrued: a.accrued + l.accrued,
        actual: a.actual + l.actual,
      }),
      { budget: 0, committed: 0, accrued: 0, actual: 0 },
    );
  }, [scopedLines]);
  const variance = totals.accrued - totals.actual;

  const stats: Stat[] = [
    { label: t("afe.kpi.total"), value: money(totals.budget, ccy, { compact: true }) },
    { label: t("afe.kpi.committed"), value: money(totals.committed, ccy, { compact: true }) },
    {
      label: t("afe.kpi.accrued"),
      value: money(totals.accrued, ccy, { compact: true }),
      tone: "ops",
    },
    { label: t("afe.kpi.actual"), value: money(totals.actual, ccy, { compact: true }) },
    {
      label: t("afe.kpi.variance"),
      value: signedMoney(variance, money, ccy),
      // accrued > actual = under-billed (good); accrued < actual = over (crit).
      tone: variance < 0 ? "crit" : "good",
    },
  ];

  const columns: InlineColumn<AfeLineOut, AfeDraft>[] = [
    {
      key: "account",
      header: t("afe.col.account"),
      width: 220,
      render: (l) => (
        <span>
          <span className="mono">{accountLabel(l.account_entry_key)}</span>
          {l.owner_party === "service" ? (
            <span className="owner-chip service" style={{ marginLeft: 6 }}>
              {t("afe.owner.service")}
            </span>
          ) : null}
        </span>
      ),
      input: (d, patch) => (
        <CatalogPicker
          catalogKey="cost_accounts"
          value={d.account}
          compact
          serverSearch
          ariaLabel={t("afe.col.account")}
          onChange={(code, value) =>
            // Treatment (tratamiento) + unit auto-fill from the account (§21f).
            patch({
              account: code,
              accountValue: value,
              unit: d.unit || String(value?.attrs?.unidad ?? ""),
            })
          }
        />
      ),
    },
    {
      key: "description",
      header: t("afe.col.description"),
      render: (l) => l.description ?? "—",
      input: (d, patch) => (
        <input
          aria-label={t("afe.col.description")}
          value={d.description}
          onChange={(e) => patch({ description: e.target.value })}
        />
      ),
    },
    {
      key: "method",
      header: t("afe.col.method"),
      width: 150,
      render: (l) => (
        <MethodBadge method={l.accrual_method} label={t(methodKey(l.accrual_method))} />
      ),
      input: (d, patch) => (
        <select
          aria-label={t("afe.col.method")}
          value={d.method}
          onChange={(e) => patch({ method: e.target.value as AccrualMethod })}
        >
          {METHODS.map((m) => (
            <option key={m.id} value={m.id}>
              {t(m.key)}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "treatment",
      header: t("afe.col.treatment"),
      computed: true,
      width: 96,
      // Treatment auto-fills from the cost account (server-owned); read-only.
      render: (l) => <span className="dim">{treatmentFor(l)}</span>,
    },
    {
      key: "budget",
      header: t("afe.col.budget"),
      num: true,
      render: (l) => money(l.budget, l.currency),
      input: (d, patch) => (
        <input
          aria-label={t("afe.col.budget")}
          inputMode="decimal"
          value={d.budget}
          onChange={(e) => patch({ budget: e.target.value })}
        />
      ),
    },
    {
      key: "committed",
      header: t("afe.col.committed"),
      num: true,
      computed: true,
      render: (l) => money(l.committed, l.currency),
    },
    {
      key: "accrued",
      header: t("afe.col.accrued"),
      num: true,
      computed: true,
      // Accrual is displayed per line exactly as the server computed it (§21f).
      render: (l) => <AccrualCell line={l} money={money} />,
    },
    {
      key: "actual",
      header: t("afe.col.actual"),
      num: true,
      computed: true,
      render: (l) => money(l.actual, l.currency),
    },
    {
      key: "variance",
      header: t("afe.col.variance"),
      num: true,
      computed: true,
      render: (l) => {
        const v = l.accrued - l.actual;
        return (
          <span className={v < 0 ? "var-bad" : "var-good"}>
            {signedMoney(v, money, l.currency)}
          </span>
        );
      },
    },
    {
      key: "status",
      header: t("afe.col.status"),
      width: 110,
      render: (l) => <span className="badge">{l.status}</span>,
    },
  ];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={scopedLines.length === 0}
      emptyMessage={t("afe.noLines")}
      onRetry={refetch}
    >
      <StatBand stats={stats} />

      {!isOperator ? (
        <div className="devengado-note" style={{ marginTop: 12 }}>
          <span>
            <strong>{t("afe.scope.service")}</strong> {t("afe.scope.serviceHint")}
          </span>
        </div>
      ) : null}

      <div className="afe-split">
        {/* LEFT — ops program sequence; selecting a step filters the grid. */}
        <div className="card">
          <div className="panel-head-row">
            <h3>{t("afe.sequence.title")}</h3>
          </div>
          <div className="afe-steps" role="tablist" aria-label={t("afe.sequence.title")}>
            {steps.map((s, i) => (
              <button
                key={s.step}
                type="button"
                role="tab"
                aria-selected={s.step === activeStep}
                className={`afe-step${s.step === activeStep ? " active" : ""}`}
                onClick={() => setSelectedStep(s.step)}
              >
                <span className="idx">{i + 1}</span>
                <span>
                  <span className="name">{s.step}</span>
                  <span className="meta">
                    {money(s.accrued, ccy, { compact: true })} {t("afe.sequence.accruedOf")}{" "}
                    {money(s.budget, ccy, { compact: true })}
                  </span>
                </span>
                <span className="amt">{money(s.budget, ccy, { compact: true })}</span>
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT — the AFE line grid for the selected step. */}
        <div className="card">
          <div className="panel-head-row">
            <h3>
              {t("afe.lines.title")}
              {activeStep ? <span className="dim"> — {activeStep}</span> : null}
            </h3>
          </div>
          <div className="devengado-note">
            <span>
              <strong>{t("afe.devengado.term")}</strong> {t("afe.devengado.hint")}
            </span>
          </div>
          <div className="table-scroll">
            <InlineRowGrid<AfeLineOut, AfeDraft>
              ariaLabel={t("afe.lines.title")}
              columns={columns}
              rows={gridLines}
              rowKey={(l) => l.id}
              rowOwner={() => "p"}
              emptyDraft={emptyDraft}
              emptyLabel={t("afe.noLines")}
              toRow={(d) => {
                // Required: an account + a positive budget. Reject otherwise.
                const budget = Number(d.budget);
                if (!d.account || !Number.isFinite(budget) || budget <= 0) return null;
                const qty = d.qty ? Number(d.qty) : null;
                // Optimistic row; the server returns the real committed/accrued.
                return {
                  id: `pending-${Date.now()}`,
                  event_id: eventId,
                  account_entry_key: d.account,
                  description: d.description || null,
                  program_step: activeStep,
                  accrual_method: d.method,
                  unit: d.unit || null,
                  qty,
                  rate: null,
                  budget,
                  committed: 0,
                  accrued: 0,
                  actual: 0,
                  status: "pending",
                  currency: ccy ?? "USD",
                  owner_party: isOperator ? "operator" : "service",
                  is_additional: false,
                };
              }}
              onCommit={(row) =>
                createLine({
                  eventId,
                  body: {
                    account_entry_key: row.account_entry_key,
                    description: row.description,
                    program_step: row.program_step,
                    accrual_method: row.accrual_method,
                    unit: row.unit,
                    qty: row.qty,
                    budget: row.budget,
                    owner_party: row.owner_party,
                  },
                })
              }
            />
          </div>
          {/* Per-step re-point control (Ramon's headline PATCH): set a line's
              accrual method after the fact. Kept simple — one row at a time. */}
          {gridLines.length ? (
            <MethodEditor
              lines={gridLines}
              methods={METHODS}
              label={t("afe.edit.method")}
              methodLabel={(m) => t(methodKey(m))}
              accountLabel={accountLabel}
              onSet={(entityId, method) =>
                updateLine({ eventId, entityId, body: { accrual_method: method } })
              }
            />
          ) : null}
        </div>
      </div>
    </QueryState>
  );
}

/** Signed money with an explicit +/− and the line/panel currency. */
function signedMoney(
  amount: number,
  money: ReturnType<typeof useMoney>["money"],
  ccy: string | null | undefined,
): string {
  const sign = amount > 0 ? "+" : amount < 0 ? "−" : "";
  return `${sign}${money(Math.abs(amount), ccy)}`;
}

const methodKey = (m: AccrualMethod): MessageKey => `afe.method.${m}` as MessageKey;

/** The colour-coded accrual-method badge. */
function MethodBadge({ method, label }: { method: AccrualMethod; label: string }) {
  return <span className={`am-badge am-${method}`}>{label}</span>;
}

/** Treatment (tratamiento) as served on the line's account — display helper. */
function treatmentFor(line: AfeLineOut): string {
  // by_quantity accrues incrementally (Avance-like); lump/milestone accrue on
  // their event (Devengado-like). Blank account defaults to Avance (§21f).
  return line.accrual_method === "by_quantity" || line.accrual_method === "pct_complete"
    ? "Avance"
    : "Devengado";
}

/**
 * Accrual cell — renders the served `accrued` with the affordance its method
 * implies: a progress bar for pct_complete, an "awaiting milestone" dot for a
 * not-yet-accrued lump/milestone line, plain money otherwise.
 */
function AccrualCell({
  line,
  money,
}: {
  line: AfeLineOut;
  money: ReturnType<typeof useMoney>["money"];
}) {
  if (line.accrual_method === "pct_complete" && line.budget > 0) {
    const pct = Math.min(100, Math.round((line.accrued / line.budget) * 100));
    return (
      <span>
        <span className="afe-pctbar">
          <span className="fill" style={{ width: `${pct}%` }} />
        </span>
        {money(line.accrued, line.currency)}
      </span>
    );
  }
  if (
    (line.accrual_method === "lump" || line.accrual_method === "milestone") &&
    line.accrued === 0
  ) {
    return (
      <span className="afe-awaiting">
        <span className="dot" />
        {money(line.accrued, line.currency)}
      </span>
    );
  }
  return <span>{money(line.accrued, line.currency)}</span>;
}

/** A compact "re-point a line's accrual method" control (PATCH). */
function MethodEditor({
  lines,
  methods,
  label,
  methodLabel,
  accountLabel,
  onSet,
}: {
  lines: AfeLineOut[];
  methods: { id: AccrualMethod; key: MessageKey }[];
  label: string;
  methodLabel: (m: AccrualMethod) => string;
  accountLabel: (code: string | null | undefined) => string;
  onSet: (entityId: string, method: AccrualMethod) => void;
}) {
  const [entityId, setEntityId] = useState<string>(lines[0]?.id ?? "");
  const current = lines.find((l) => l.id === entityId) ?? lines[0];
  return (
    <div className="panel-head-row" style={{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
      <label className="dim" style={{ fontSize: 12 }}>
        {label}
      </label>
      <select
        aria-label={label}
        value={current?.id ?? ""}
        onChange={(e) => setEntityId(e.target.value)}
      >
        {lines.map((l) => (
          <option key={l.id} value={l.id}>
            {accountLabel(l.account_entry_key)} · {l.description ?? ""}
          </option>
        ))}
      </select>
      <div className="seg" role="group" aria-label={label}>
        {methods.map((m) => (
          <button
            key={m.id}
            type="button"
            aria-pressed={current?.accrual_method === m.id}
            className={current?.accrual_method === m.id ? "active" : ""}
            onClick={() => current && onSet(current.id, m.id)}
          >
            {methodLabel(m.id)}
          </button>
        ))}
      </div>
    </div>
  );
}
